import { Currency } from "@redotech/money/currencies";
import { type UsdDollars } from "@redotech/money/usd-amounts";
import { getCurrencyFormatter } from "@redotech/money/util";
import {
  ConversionBillingTrialType,
  isSelfServeBillingTrial,
} from "@redotech/redo-billing-common/checkout-optimization-billing-schema";
import {
  getUsagePlanPricing,
  ShopifySubscription,
} from "@redotech/redo-shopify-common/shopify-subscription";
import { SlideInPanelAnchorLocation } from "@redotech/redo-storefront-common/slide-in-panel-anchor-location";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const discountCombinesWithSchema_ = z.object({
  orderDiscounts: z.boolean().optional(),
  productDiscounts: z.boolean().optional(),
  shippingDiscounts: z.boolean().optional(),
});
export const discountCombinesWithSchema: z.ZodType<DiscountCombinesWith> =
  discountCombinesWithSchema_;
export interface DiscountCombinesWith extends z.infer<
  typeof discountCombinesWithSchema_
> {}

const baseDiscountSchema = z.object({
  autoGenerateRandomCode: z.boolean().optional(),
  title: z.string().optional(),
  combinesWith: discountCombinesWithSchema.optional(),
});

const fixedAmountDiscountSchema = baseDiscountSchema.extend({
  discountType: z.literal("fixed_amount"),
  amount: z.object({
    currencyCode: createEnum(Currency, { enumName: "Currency" }),
    value: z.string(),
  }),
});

const percentageDiscountSchema = baseDiscountSchema.extend({
  discountType: z.literal("percentage"),
  percentage: z.string(),
});

export const rescueModalDiscountSchema = z.union([
  fixedAmountDiscountSchema,
  percentageDiscountSchema,
]);
export type RescueModalDiscount = z.infer<typeof rescueModalDiscountSchema>;

export const RESCUE_MODAL_UI_BUTTON_SUBTEXT_DEFAULT = {
  shouldShow: "if_coverage_eligible" as const,
  value: "(Checkout+ included)",
};
export function getDefaultRescueModalTitle(
  discount: RescueModalDiscount | undefined,
): string {
  function formatDiscount(discount: RescueModalDiscount): string {
    if (discount.discountType === "percentage") {
      return `${discount.percentage}%`;
    }
    const formatter = getCurrencyFormatter(discount.amount.currencyCode);
    return formatter.format(parseFloat(discount.amount.value));
  }

  if (!discount) {
    return "Your discount is waiting";
  }
  return `Your ${formatDiscount(discount)} discount is waiting`;
}
export const DEFAULT_RESCUE_MODAL_DESCRIPTION =
  "Go to checkout with the button below to complete your purchase and save on this order.";
export const DEFAULT_RESCUE_MODAL_BUTTON_TEXT = "Apply discount";
export const DEFAULT_RESCUE_MODAL_PLACEMENT =
  SlideInPanelAnchorLocation.TOP_LEFT;

export const rescueModalUiButtonSubtextSchema = z.union([
  z.object({
    shouldShow: z.union([
      z.literal("always"),
      z.literal("if_coverage_eligible"),
    ]),
    value: z.string().optional(),
  }),
  z.object({ shouldShow: z.literal("never") }),
]);
export type RescueModalUiButtonSubtext = z.infer<
  typeof rescueModalUiButtonSubtextSchema
>;

export const rescueModalUiSchema = z.object({
  button: z
    .object({
      text: z.string().optional(),
      subtext: rescueModalUiButtonSubtextSchema.optional(),
    })
    .optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  placement: createEnum(SlideInPanelAnchorLocation, {
    enumName: "SlideInPanelAnchorLocation",
  }).optional(),
});
export type RescueModalUi = z.infer<typeof rescueModalUiSchema>;

export const DEFAULT_DROPOFF_PERCENTAGE_TRIGGER = 50;

/**
 * Default inclusive minimum cart quantity for the no-model "stale page"
 * fallback trigger. The cart's total item quantity must be `>=` this for the
 * fallback to fire. Defaults to 3, reproducing the historical `> 2` rule.
 * A team can lower this (e.g. to 0) so the fallback fires regardless of cart
 * contents — required for buy-now / empty-cart storefronts, paired with
 * `allowEmptyCart`.
 */
export const DEFAULT_STALE_PAGE_MIN_CART_ITEMS = 3;

export const rescueModalSchema = z.object({
  enabled: z.boolean(),
  discountCode: z.string().optional(),
  discount: rescueModalDiscountSchema.optional(),
  dropoffPercentageTrigger: z.number().min(0).max(100).optional(),
  secondsInColdTrigger: z.number().optional(),
  /**
   * When true, the rescue panel / spotlight may show even with an empty cart.
   * Defaults to false (the panel is suppressed when the cart is empty). Set
   * per-team in the DB for buy-now storefronts where the cart is empty until
   * checkout. No merchant-app UI yet — edited directly on the team document.
   */
  allowEmptyCart: z.boolean().optional(),
  /**
   * Inclusive minimum cart quantity for the no-model stale-page fallback
   * trigger. Defaults to {@link DEFAULT_STALE_PAGE_MIN_CART_ITEMS}. Set to 0
   * (with {@link allowEmptyCart}) so the fallback fires regardless of cart
   * contents. No merchant-app UI yet — edited directly on the team document.
   */
  stalePageMinCartItems: z.number().int().min(0).optional(),
  ui: rescueModalUiSchema.optional(),
});
export type RescueModal = z.infer<typeof rescueModalSchema>;

export const rescueModalDefault: RescueModal = { enabled: false };

/** Prefix for Multi-Model Endpoint names (e.g., checkout-hazard-mme-001) */
export const MME_ENDPOINT_NAME_PREFIX = "checkout-hazard-mme";

/** Default MME endpoint name for checkout hazard model */
export const DEFAULT_MME_ENDPOINT_NAME =
  `${MME_ENDPOINT_NAME_PREFIX}-001` as const;

export const activeCheckoutHazardModelSchema = z.object({
  endpointName: z.string(),
  modelVersion: z.enum(["v-08-11-25", "v-11-25-25"]).default("v-08-11-25"),
});
export type ActiveCheckoutHazardModel = z.infer<
  typeof activeCheckoutHazardModelSchema
>;

export const checkoutHazardBuildStatusEnum = z.enum([
  "succeeded",
  "failed",
  "insufficient_data",
  "running",
]);
export type CheckoutHazardBuildStatus = z.infer<
  typeof checkoutHazardBuildStatusEnum
>;

/**
 * Snowflake-observed volume/recency metrics used by the data sufficiency gate.
 * Persisted on `insufficient_data` build results so the admin UI can render
 * the specific blocker (e.g. "610/10,000 sessions") instead of an opaque
 * "queued for training" forever.
 */
const hazardDataMetricsSchema_ = z.object({
  totalSessions: z.number(),
  convertedSessions: z.number(),
  dataSpanDays: z.number(),
});
export const hazardDataMetricsSchema: z.ZodType<HazardDataMetrics> =
  hazardDataMetricsSchema_;
export interface HazardDataMetrics extends z.infer<
  typeof hazardDataMetricsSchema_
> {}

/**
 * Minimum data volume required for the hazard model to train. Enforced by
 * `checkDataSufficiencyActivity` against the Snowflake `hazard_buckets_15s`
 * table (60-day lookback). Values below any one of these thresholds produce
 * an `insufficient_data` build result.
 *
 * Centralized here so the activity, the admin UI, and the Zod schema all
 * reference the same source of truth.
 */
export const HAZARD_DATA_SUFFICIENCY_THRESHOLDS: HazardDataMetrics = {
  totalSessions: 10_000,
  convertedSessions: 500,
  dataSpanDays: 14,
};

const checkoutHazardBuildResultSchema_ = z.object({
  status: checkoutHazardBuildStatusEnum,
  completedAt: z.date(),
  failureCount: z.number().optional(),
  /**
   * Present only on `status === "insufficient_data"` results. Captures the
   * actual Snowflake metrics observed at the time the sufficiency gate
   * rejected the team, so the admin UI can show the delta against
   * {@link HAZARD_DATA_SUFFICIENCY_THRESHOLDS}.
   */
  metrics: hazardDataMetricsSchema.optional(),
  /**
   * Present only on `status === "running"` results. The ARN of the AWS Step
   * Function execution so the next workflow run can check whether it completed
   * (succeeded / failed) while the previous Temporal workflow was timed out.
   */
  executionArn: z.string().optional(),
});
export const checkoutHazardBuildResultSchema: z.ZodType<CheckoutHazardBuildResult> =
  checkoutHazardBuildResultSchema_;
export interface CheckoutHazardBuildResult extends z.infer<
  typeof checkoutHazardBuildResultSchema_
> {}

export const predictiveConversionSettingsSchema = z.object({
  enabled: z.boolean(),
  dataCollectionStartedAt: z.date().optional(),
  intentRequested: z.boolean().optional(),
  activeCheckoutHazardModel: activeCheckoutHazardModelSchema.optional(),
  checkoutHazardBuildResult: checkoutHazardBuildResultSchema.optional(),
  rescueModal: rescueModalSchema.optional(),
  intentActivatedAt: z.date().optional(),
});
export type PredictiveConversionSettings = z.infer<
  typeof predictiveConversionSettingsSchema
>;

export const predictiveConversionSettingsDefault: PredictiveConversionSettings =
  { enabled: false };

/**
 * Flat monthly price for the intent rescue popup, in USD. Stored on the
 * team document as a `checkoutOptimizationBilling.subscription` record with
 * `periodFeeInCents` derived via `dollarsToCents` from this value, matching
 * the existing chop subscription billing pattern. Shared so wizard copy
 * and the webhook write stay in sync.
 *
 * Branded as `UsdDollars` so the billing service can hand it straight to
 * `dollarsToCents` without re-casting.
 */
export const INTENT_RESCUE_MONTHLY_PRICE_USD = 99 as UsdDollars;

/** Attribution rate for Recover-onboarded intent rescue: % of the subtotal of
 * orders where the shopper clicked the rescue CTA. */
export const RECOVER_INTENT_ATTRIBUTION_PERCENT = 2.5;

/**
 * Length of the free trial granted to self-serve intent-rescue merchants.
 * Billing is deferred by setting the chop subscription's `startDate` this far
 * into the future, so the first invoice period only begins once the trial
 * ends. Shared so the wizard copy and the activation write stay in sync.
 */
export const INTENT_RESCUE_FREE_TRIAL_DAYS = 14;

/**
 * Minimum *annualized* recovered revenue (in USD) a trial must project before
 * the trial-expired lock page showcases the revenue-lift stat. Below this the
 * number undersells the product, so the stat is omitted entirely.
 */
export const INTENT_TRIAL_REVENUE_SHOWCASE_MIN_USD = 1200;

/**
 * Project the revenue recovered over the free-trial window out to a full
 * year: `trialRevenue * (365 / trialDays)`. The trial-expired page shows this
 * annualized figure so a two-week sample reads as the run-rate it implies
 * rather than a small absolute number.
 */
export function annualizeTrialRevenue(
  trialRevenueUsd: number,
  trialDays: number = INTENT_RESCUE_FREE_TRIAL_DAYS,
): number {
  return trialRevenueUsd * (365 / trialDays);
}

/**
 * Whether the trial-expired lock page should surface the recovered-revenue
 * stat. Takes the already-annualized figure (see {@link annualizeTrialRevenue})
 * and requires it to be strictly greater than
 * {@link INTENT_TRIAL_REVENUE_SHOWCASE_MIN_USD} — exactly $1200 is still
 * omitted.
 */
export function shouldShowTrialRevenueLift(
  annualizedRevenueUsd: number,
): boolean {
  return annualizedRevenueUsd > INTENT_TRIAL_REVENUE_SHOWCASE_MIN_USD;
}

/**
 * Minimal structural view of a team document needed to evaluate
 * {@link isIntentTrialExpiredUnpaid}. Both the client-side `Team` model and
 * the backend `TeamRaw` / `TeamHydrated` mongoose types satisfy it, so the
 * merchant app and the storefront-serving backend share one predicate.
 * `startDate` admits a string because client team docs arrive as JSON.
 */
export interface IntentTrialTeamLike {
  settings: {
    predictiveConversion?: { enabled: boolean } | null;
    checkoutOptimization?: {
      enabled?: boolean | null;
      checkoutOptimizationBilling?: {
        startDate?: Date | string | null;
        /**
         * Self-serve free-trial product path on chop billing.
         * See {@link ConversionBillingTrialType}.
         */
        trialType?: ConversionBillingTrialType | null;
      } | null;
    } | null;
    shopifySubscription?: ShopifySubscription[] | null;
  };
}

/**
 * True iff a self-serve intent-rescue team's free trial has lapsed without
 * the merchant ever approving billing in Shopify. The chop billing record's
 * `startDate` (written by activateIntentRescue as now + trial length) doubles
 * as the trial-end timestamp — there is intentionally no separate
 * `trialEndsAt` field.
 *
 * "Billing accepted" = active Shopify usage plan on shopifySubscription[0]
 * (same signal as isConversionBillingAccepted). That alone clears the wall.
 *
 * Who is on the self-serve trial path (locks are NEVER used here — they are
 * presentation-only):
 * - Classic: Conversion off + predictive on (pre-migration intent-only)
 * - New / migrated: checkoutOptimizationBilling.trialType is INTENT or INTENT_PPU
 *
 * Engine-billed full customers with admin presentation locks are therefore
 * never trial-locked: they are Conversion-on without a self-serve trialType.
 *
 * Deploy sequencing: backfill `trialType` (e.g. intent-ppu) for pre-existing
 * self-serve intent-only teams (Conversion on + intent-only locks) atomically
 * with, or strictly before, this predicate goes live. Otherwise a migrated
 * team with conversionEnabled && !selfServeTrial would skip the unpaid-trial
 * wall (paywall bypass) until the marker is written.
 *
 * Boundary: `now` exactly equal to `startDate` is still within the trial.
 */
export function isIntentTrialExpiredUnpaid({
  team,
  now = new Date(),
}: {
  team: IntentTrialTeamLike;
  now?: Date;
}): boolean {
  if (team.settings.predictiveConversion?.enabled !== true) {
    return false;
  }
  // Billing accepted (Shopify usage plan) — paid self-serve or umbrella sub.
  if (getUsagePlanPricing(team.settings.shopifySubscription?.[0])) {
    return false;
  }

  const checkoutOptimization = team.settings.checkoutOptimization;
  const conversionEnabled = checkoutOptimization?.enabled === true;
  const selfServeTrial = isSelfServeBillingTrial(
    checkoutOptimization?.checkoutOptimizationBilling?.trialType,
  );
  // Classic intent-only (Conversion off) or explicit self-serve trialType.
  // Do not use lockedFeatures — presentation locks must not cut storefront.
  // See deploy sequencing note above re: conversionEnabled && !selfServeTrial.
  if (conversionEnabled && !selfServeTrial) {
    return false;
  }

  const startDate =
    checkoutOptimization?.checkoutOptimizationBilling?.startDate;
  if (!startDate) {
    return false;
  }
  if (now.getTime() <= new Date(startDate).getTime()) {
    return false;
  }
  return true;
}

/** Minimum days of checkout data required before the hazard model is trainable. */
export const MIN_DATA_COLLECTION_DAYS = 14;

/**
 * Number of consecutive retrain failures after which the UI surfaces a hard
 * "Training failed" state instead of "Retry scheduled". The build pipeline
 * itself keeps retrying with Fibonacci backoff regardless — this is purely a
 * presentational threshold.
 */
export const MAX_INTENT_FAILURE_COUNT_BEFORE_FAILED = 5;

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Fibonacci-day backoff for checkout hazard model build retries. Used by the
 * cron task to gate retry eligibility and by the admin UI to forecast the next
 * attempt date.
 */
export function fibonacciDays(n: number): number {
  if (n <= 1) {
    return 0;
  }
  let a = 0;
  let b = 1;
  for (let i = 0; i < n; i++) {
    [a, b] = [b, a + b];
  }
  return a;
}

/**
 * Returns the next run time for the daily checkout-hazard build readiness cron
 * (`0 3 * * *`). If `now` is before 03:00 UTC today, returns today at 03:00
 * UTC; otherwise tomorrow at 03:00 UTC.
 */
export function nextBuildReadinessCronRun(now: Date): Date {
  const next = new Date(now);
  next.setUTCHours(3, 0, 0, 0);
  if (next.getTime() <= now.getTime()) {
    next.setUTCDate(next.getUTCDate() + 1);
  }
  return next;
}

/**
 * Derived admin-UI status for a team's predictive conversion / intent pipeline.
 * The primary dimension is whether `activeCheckoutHazardModel.endpointName` is
 * set — that's the source of truth for "are we actually serving predictions."
 * Everything else is secondary signal about freshness / retrain health.
 */
export const intentStatusSchema = z.discriminatedUnion("status", [
  // Endpoint set → team is serving predictions.
  z.object({
    status: z.literal("live"),
    /**
     * When the currently-serving model was trained — sourced from the most
     * recent successful build's `completedAt`. Optional because we don't have
     * that timestamp for pre-existing teams activated before the build-result
     * tracking was added.
     */
    modelServingSince: z.date().optional(),
  }),
  z.object({
    status: z.literal("live_retrain_failing"),
    failureCount: z.number(),
    lastFailureAt: z.date(),
  }),
  // Endpoint not set → team is not serving predictions.
  z.object({
    status: z.literal("not_requested"),
    /**
     * The earliest date a team could reach `live` if they flipped the toggle
     * right now — `max(dataCollectionStartedAt ?? now, now) + 14 days`, give or
     * take one cron tick.
     */
    earliestLiveAt: z.date(),
  }),
  z.object({
    status: z.literal("collecting_data"),
    eligibleToTrainAt: z.date(),
  }),
  z.object({
    status: z.literal("queued_for_training"),
    nextCronRunAt: z.date(),
  }),
  /**
   * A build workflow is currently running — the Step Function has been started
   * and the workflow is either actively polling or timed out (with the next
   * daily cron run set to recover the result).
   */
  z.object({
    status: z.literal("training"),
    startedAt: z.date(),
    nextCronRunAt: z.date(),
  }),
  /**
   * The team is eligible to train (data collection window satisfied, intent
   * requested, no active endpoint), the workflow ran, and the Snowflake
   * sufficiency gate rejected them. Persists the observed metrics so the
   * admin UI can show exactly which threshold is the blocker and by how much.
   */
  z.object({
    status: z.literal("insufficient_data"),
    metrics: hazardDataMetricsSchema,
    thresholds: hazardDataMetricsSchema,
    lastCheckedAt: z.date(),
    nextCronRunAt: z.date(),
  }),
  z.object({
    status: z.literal("retry_scheduled"),
    nextAttemptAt: z.date(),
    failureCount: z.number(),
  }),
  z.object({ status: z.literal("failed"), failureCount: z.number() }),
]);
export type IntentStatus = z.infer<typeof intentStatusSchema>;

/**
 * Compute the admin-UI intent status from raw team settings. Pure function —
 * takes `now` as a parameter so it's trivially testable.
 */
export function computeIntentStatus({
  now,
  settings,
}: {
  now: Date;
  settings: PredictiveConversionSettings | undefined;
}): IntentStatus {
  const endpointSet = !!settings?.activeCheckoutHazardModel?.endpointName;
  const buildResult = settings?.checkoutHazardBuildResult;
  const intentRequested = settings?.intentRequested ?? false;
  const dataCollectionStartedAt = settings?.dataCollectionStartedAt;

  // Primary dimension: is the endpoint serving predictions?
  if (endpointSet) {
    if (buildResult?.status === "failed") {
      // Last retrain failed, but the previously-trained model is still
      // serving. We don't record the last *successful* build timestamp, so
      // we intentionally omit a "serving since" date for this variant.
      return {
        status: "live_retrain_failing",
        failureCount: buildResult.failureCount ?? 1,
        lastFailureAt: buildResult.completedAt,
      };
    }
    return {
      status: "live",
      // Only report `modelServingSince` when we have a verified successful
      // build timestamp. Falling back to `intentActivatedAt` or `now` would
      // be misleading (intentActivatedAt is "first ever activation," which
      // can be months before the currently-serving model was trained).
      modelServingSince:
        buildResult?.status === "succeeded"
          ? buildResult.completedAt
          : undefined,
    };
  }

  // Not serving. Forecast the earliest date this team could go live.
  const eligibleBaseline = dataCollectionStartedAt ?? now;
  const earliestEligibleToTrainAt = new Date(
    eligibleBaseline.getTime() + MIN_DATA_COLLECTION_DAYS * MS_PER_DAY,
  );
  const dataCollectionSatisfied =
    earliestEligibleToTrainAt.getTime() <= now.getTime();

  if (!intentRequested) {
    // If data collection is already satisfied, the bottleneck is the next
    // cron tick; otherwise it's the data-collection window.
    return {
      status: "not_requested",
      earliestLiveAt: dataCollectionSatisfied
        ? nextBuildReadinessCronRun(now)
        : earliestEligibleToTrainAt,
    };
  }

  // Intent is requested but endpoint not set.
  if (!dataCollectionSatisfied) {
    return {
      status: "collecting_data",
      eligibleToTrainAt: earliestEligibleToTrainAt,
    };
  }

  // Eligible to train. Check build result for retry state.
  if (buildResult?.status === "failed") {
    const failureCount = buildResult.failureCount ?? 1;
    if (failureCount >= MAX_INTENT_FAILURE_COUNT_BEFORE_FAILED) {
      return { status: "failed", failureCount };
    }
    const backoffDays = fibonacciDays(failureCount);
    const nextAttemptAt = new Date(
      buildResult.completedAt.getTime() + backoffDays * MS_PER_DAY,
    );
    return { status: "retry_scheduled", nextAttemptAt, failureCount };
  }

  // A build workflow is actively running (or timed out with the Step Function
  // still in progress). Show "training" instead of the misleading "queued."
  if (buildResult?.status === "running") {
    return {
      status: "training",
      startedAt: buildResult.completedAt,
      nextCronRunAt: nextBuildReadinessCronRun(now),
    };
  }

  // The workflow ran but the Snowflake sufficiency gate rejected this team.
  // Surface the specific blocker rather than lying about being "queued."
  // Missing `metrics` would indicate a pre-patch write; fall through to
  // queued_for_training in that case so the UI stays coherent.
  if (buildResult?.status === "insufficient_data" && buildResult.metrics) {
    return {
      status: "insufficient_data",
      metrics: buildResult.metrics,
      thresholds: HAZARD_DATA_SUFFICIENCY_THRESHOLDS,
      lastCheckedAt: buildResult.completedAt,
      nextCronRunAt: nextBuildReadinessCronRun(now),
    };
  }

  // Eligible, no failed/insufficient build on record, endpoint not set.
  // Waiting for the next cron run to kick off a build workflow.
  return {
    status: "queued_for_training",
    nextCronRunAt: nextBuildReadinessCronRun(now),
  };
}
